// src/views/ProductQuoteView.js
// ============================================================================
// COTIZADOR MULTIPIEZA — producto complejo = Σ N piezas (cada una con su material,
// modo, geometría y piezasPorProducto) + extras (mano de obra / material / ...).
// "Cliente tonto, servidor inteligente": el costo lo calcula `getProductQuote`
// (computeProductCost) en el servidor. Permiso: canCreateQuotes.
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseDxf } from '../lib/dxfParse';
import { buildSpecSheetPdf } from '../lib/specSheet';
import { buildEstimateHtml, downloadPdfBase64, impositionSvgString } from '../lib/estimatePdf.mjs';
import { MATERIAL_TEMPLATES } from '../lib/materialTemplates.mjs';
import { CostComponentRows } from '../components/CostBreakdown';
import ImpositionSVG from '../components/ImpositionSVG';

const QUALITIES = [{ value: 'fast', label: 'Fast' }, { value: 'normal', label: 'Normal' }, { value: 'high', label: 'High' }];
const money = (n) => (typeof n === 'number' ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');
const moneyU = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;

// Tipos de extra por RENDIMIENTO (B4). Cada uno deriva su costo POR UNIDAD DE PRODUCTO.
const EXTRA_TIPOS = [
  { value: 'mano-de-obra', label: 'Mano de obra (por hora)' },
  { value: 'rendimiento-unidad', label: 'Rendimiento por unidad (pega/herrajes)' },
  { value: 'rendimiento-medida', label: 'Rendimiento por medida (tape/fleje/film)' },
];
// Mirror EXACTO de extraLineUnitCost (functions/lib/quote.js) — el servidor manda; esto es solo el preview en vivo.
const extraUn = (x) => {
  const n = (v) => Number(v) || 0;
  if (x.tipo === 'mano-de-obra') { const t = n(x.tarifa) > 0 ? n(x.tarifa) : 25; const pph = n(x.productosPorHora); return pph > 0 ? t / pph : 0; }
  if (x.tipo === 'rendimiento-unidad') { const r = n(x.productosQueRinde); return r > 0 ? n(x.costoEnvase) / r : 0; }
  if (x.tipo === 'rendimiento-medida') { const m = n(x.medidaRollo); return m > 0 ? (n(x.costoRollo) / m) * n(x.consumoPorProducto) : 0; }
  return 0;
};

const newPiece = (n = 1) => ({
  nombre: `Pieza ${n}`, materialId: '', familia: '', fluteSel: '', geometryMode: '1up', carril: 'STOCK', dxfText: '', dxfName: '',
  ups: 9, ruleLengthPerSheet: 2500, gap: 0.5, nestingUps: 9,
  modo: 'produccion', blanco: false, sides: 1, cutEnabled: true, printEnabled: true, piezasPorProducto: 1,
  extras: [],   // extras de rendimiento PROPIOS de esta pieza (B4 nivel 1)
});
const newExtra = () => ({
  descripcion: '', tipo: 'mano-de-obra',
  tarifa: 25, productosPorHora: '',          // mano de obra
  costoEnvase: '', productosQueRinde: '',     // rendimiento por unidad
  costoRollo: '', medidaRollo: '', consumoPorProducto: '', // rendimiento por medida
});

// Editor reutilizable de líneas de extra por RENDIMIENTO. Se usa por PIEZA y a nivel PRODUCTO (armado).
const ExtrasEditor = ({ lines, onAdd, onUpd, onRemove, title, hint, totalLabel }) => {
  const total = lines.reduce((s, x) => s + extraUn(x), 0);
  const nIn = (i, k, ph, w = 'w-16') => (
    <input type="number" min="0" value={lines[i][k] != null ? lines[i][k] : ''} placeholder={ph}
      onChange={(e) => onUpd(i, k, e.target.value)} className={`${w} p-1.5 border border-gray-200 rounded text-center`} />
  );
  return (
    <div className="border border-gray-100 rounded-lg p-2 bg-gray-50/60">
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-xs font-semibold text-gray-700">{title}</h4>
        <button type="button" onClick={onAdd} className="text-xs px-2 py-0.5 border rounded bg-white">+ línea</button>
      </div>
      {lines.length === 0 && <p className="text-xs text-gray-400">{hint}</p>}
      {lines.map((x, i) => (
        <div key={i} className="border border-gray-100 rounded p-1.5 mb-1.5 bg-white">
          <div className="flex items-center gap-2 mb-1">
            <input value={x.descripcion} onChange={(e) => onUpd(i, 'descripcion', e.target.value)} placeholder="Descripción"
              className="flex-1 p-1 border border-gray-200 rounded text-sm" />
            <select value={x.tipo} onChange={(e) => onUpd(i, 'tipo', e.target.value)} className="p-1 border border-gray-200 rounded text-xs">
              {EXTRA_TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <button type="button" onClick={() => onRemove(i)} className="text-red-600 text-xs px-1">✕</button>
          </div>
          <div className="flex items-center flex-wrap gap-1 text-xs text-gray-600">
            {x.tipo === 'mano-de-obra' && (<><span>$</span>{nIn(i, 'tarifa', '25')}<span>/h ÷</span>{nIn(i, 'productosPorHora', 'prod/h', 'w-20')}<span>prod/h</span></>)}
            {x.tipo === 'rendimiento-unidad' && (<><span>$</span>{nIn(i, 'costoEnvase', '40')}<span>÷</span>{nIn(i, 'productosQueRinde', '200', 'w-20')}<span>rinde</span></>)}
            {x.tipo === 'rendimiento-medida' && (<><span>$</span>{nIn(i, 'costoRollo', '8')}<span>÷</span>{nIn(i, 'medidaRollo', '1800', 'w-20')}<span>×</span>{nIn(i, 'consumoPorProducto', '24')}<span>cons.</span></>)}
            <span className="ml-auto font-semibold text-emerald-700 whitespace-nowrap">= {moneyU(extraUn(x))}/un</span>
          </div>
        </div>
      ))}
      {lines.length > 0 && (
        <div className="flex justify-between text-xs font-semibold text-gray-700 border-t pt-1">
          <span>{totalLabel}</span><span className="text-emerald-700">{moneyU(total)}/un</span>
        </div>
      )}
    </div>
  );
};

const ProductQuoteView = ({ db, user, fastMode = false }) => {
  const functions = useMemo(() => getFunctions(getApp(), 'us-central1'), []);
  const navigate = useNavigate();
  const location = useLocation();

  const [materials, setMaterials] = useState([]);
  const [clients, setClients] = useState([]);
  const [qtyProducto, setQtyProducto] = useState(100);
  const [pieces, setPieces] = useState([newPiece(1)]);
  const [extras, setExtras] = useState([]);
  // Cabecera (Fase 1 §2): cliente/trabajo/descripción ANTES de las piezas. El idioma (C1)
  // afecta la descripción mejorada por IA y los labels del PDF ('en' = default histórico).
  const [est, setEst] = useState({ clientId: '', jobName: '', markup: 30, description: '', descriptionLang: 'en' });
  // Borrador versionado (Fase 1 §3): id/estado del draft en curso. null = nunca guardado.
  const [draft, setDraft] = useState({ id: null, status: null, savedVersion: null, msg: '' });
  const [ia, setIa] = useState({ busy: false, suggestion: '' });
  const [autoQuote, setAutoQuote] = useState(false);   // retomar: dispara recosteo al cargar (Slice 1 sourcing)

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [estResult, setEstResult] = useState(null);
  const [estError, setEstError] = useState('');
  // Flujo OBLIGATORIO por pasos: input → preview (validar imposiciones/flauta) → precio.
  const [step, setStep] = useState('input');     // 'input' | 'preview' | 'price'
  const [fluteOk, setFluteOk] = useState(false);  // check de flauta (si alguna pieza es corrugado)
  const [rotations, setRotations] = useState({}); // rotación manual 90° por pieza (orden 2.1)

  // Cualquier cambio en piezas/cantidad/extras INVALIDA el preview validado → vuelve a 'input'.
  useEffect(() => {
    setStep('input'); setResult(null); setFluteOk(false); setRotations({}); setEstResult(null); setEstError('');
  }, [pieces, qtyProducto, extras]);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'materials'));
        setMaterials(snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          // Excluir materiales desactivados (active:false). active ausente/true => visible.
          .filter((m) => m.active !== false && m.dimensions && m.dimensions.width && m.dimensions.height)
          .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (e) { setError('No se pudieron cargar los materiales.'); }
      try {
        const c = await getDocs(collection(db, 'clients'));
        setClients(c.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { /* best-effort */ }
    })();
  }, [db]);

  // ── Fase 1 Slice 4: RETOMAR / DUPLICAR — ?draft=<id>[&version=vN] carga el borrador y
  //    reconstruye el intake EXACTO de esa versión (la última si no se indica). Es una
  //    acción deliberada desde "En curso" (EstimatesView); solo LECTURA, cero escritura.
  //    Duplicar = retomar vN como base: al "Guardar versión" nace vN+1.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const draftId = params.get('draft');
    if (!draftId) return;
    (async () => {
      try {
        const dSnap = await getDoc(doc(db, 'estimates', draftId));
        if (!dSnap.exists()) { setError('El borrador indicado no existe.'); return; }
        const d = dSnap.data();
        const vLabel = params.get('version') || (d.currentVersion ? `v${d.currentVersion}` : null);
        setEst((p) => ({ ...p, clientId: d.clientId || '', jobName: d.jobName || '',
          description: d.description || '', descriptionLang: d.descriptionLang === 'es' ? 'es' : 'en' }));
        setDraft({ id: draftId, status: d.status, savedVersion: vLabel,
          msg: vLabel ? `Retomado sobre ${vLabel} — al guardar nace la versión siguiente.` : 'Borrador retomado (sin versiones aún).' });
        if (!vLabel) return;
        const vSnap = await getDoc(doc(db, 'estimates', draftId, 'versions', vLabel));
        if (!vSnap.exists()) { setError(`La versión ${vLabel} no existe en el borrador.`); return; }
        const v = vSnap.data();
        const intake = v.intake || {};
        setQtyProducto(intake.qtyProducto || v.quantity || 100);
        setExtras(intake.extras || []);
        const payloadPieces = intake.pieces || [];
        if (payloadPieces.length) setPieces(payloadPieces.map((p, i) => pieceFromPayload(p, i + 1)));
        if (v.markup != null) setEst((p) => ({ ...p, markup: Math.round(Number(v.markup) * 100) }));
        // Rotaciones 90° del snapshot: el invalidador de preview las resetea al cambiar pieces
        // (mismo commit) → se restauran en el tick siguiente, después de esa limpieza.
        const rots = {};
        payloadPieces.forEach((p, i) => { if (p.rotate90) rots[i] = true; });
        if (Object.keys(rots).length) setTimeout(() => setRotations(rots), 0);
        // Slice 1 sourcing (bug 2): al retomar, el precio no cargaba — se dispara el
        // recosteo fiel automáticamente (flujo preview→precio; la flauta se sigue
        // confirmando a mano si hay corrugado con DXF: regla de negocio intacta).
        setTimeout(() => setAutoQuote(true), 50);
      } catch (e) { setError(e.message || 'No se pudo retomar el borrador.'); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, db]);

  // ── piezas ──
  const updPiece = (i, k, v) => setPieces((ps) => ps.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const addPiece = () => setPieces((ps) => [...ps, newPiece(ps.length + 1)]);
  const removePiece = (i) => setPieces((ps) => (ps.length > 1 ? ps.filter((_, j) => j !== i) : ps));
  const duplicatePiece = (i) => setPieces((ps) => {
    const c = { ...ps[i], nombre: `${ps[i].nombre} (copia)` };
    return [...ps.slice(0, i + 1), c, ...ps.slice(i + 1)];
  });
  const onDxf = (i, e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => { updPiece(i, 'dxfText', String(r.result || '')); updPiece(i, 'dxfName', f.name); };
    r.readAsText(f);
  };

  // ── extras POR PIEZA (B4 nivel 1): viven en pieces[i].extras ──
  const addPieceExtra = (i) => setPieces((ps) => ps.map((p, j) => (j === i ? { ...p, extras: [...(p.extras || []), newExtra()] } : p)));
  const updPieceExtra = (i, j, k, v) => setPieces((ps) => ps.map((p, pi) => (pi === i ? { ...p, extras: p.extras.map((x, xi) => (xi === j ? { ...x, [k]: v } : x)) } : p)));
  const removePieceExtra = (i, j) => setPieces((ps) => ps.map((p, pi) => (pi === i ? { ...p, extras: p.extras.filter((_, xi) => xi !== j) } : p)));

  // ── extras DE PRODUCTO (B4 nivel 2): armado/ensamble final que une las piezas ──
  const updExtra = (i, k, v) => setExtras((xs) => xs.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const addExtra = () => setExtras((xs) => [...xs, newExtra()]);
  const removeExtra = (i) => setExtras((xs) => xs.filter((_, j) => j !== i));

  // Arma el payload de una pieza para el servidor (incl. sus extras de rendimiento). `rot` = rotación
  // manual 90° de esta pieza (orden 2.1, solo 1UP).
  const piecePayload = (p, rot) => {
    const base = {
      nombre: p.nombre, materialId: p.materialId, modo: p.modo, blanco: !!p.blanco,
      sides: Number(p.sides), cutEnabled: !!p.cutEnabled, printEnabled: !!p.printEnabled,
      quality: 'fast', setupLevel: 1, factorL: 1, piezasPorProducto: Number(p.piezasPorProducto) || 1,
      carril: p.carril === 'A_LA_MEDIDA' ? 'A_LA_MEDIDA' : 'STOCK',   // carril por modo (orden 1)
      extras: p.extras || [],
    };
    // dxfName viaja en el payload SOLO para el snapshot de versión (Retomar lo restaura);
    // el server lo ignora al costear.
    if (p.geometryMode === '1up') return { ...base, dxf: p.dxfText, dxfName: p.dxfName || undefined, gap: Number(p.gap) || undefined, rotate90: !!rot };
    if (p.geometryMode === 'nesting') return { ...base, dxf: p.dxfText, dxfName: p.dxfName || undefined, dxfMode: 'nesting', ups: Number(p.nestingUps), gap: Number(p.gap) || undefined };
    return { ...base, ups: Number(p.ups), ruleLengthPerSheet: Number(p.ruleLengthPerSheet) };
  };

  // ── Fase 1 Slice 4 (Retomar/Duplicar): payload de snapshot → forma de pieza de la UI.
  //    Inversa exacta de piecePayload: reproduce el intake tal cual se cotizó (criterio #4).
  const pieceFromPayload = (p, n) => ({
    nombre: p.nombre || `Pieza ${n}`, materialId: p.materialId || '',
    geometryMode: p.dxf ? (p.dxfMode === 'nesting' ? 'nesting' : '1up') : 'manual',
    dxfText: p.dxf || '', dxfName: p.dxfName || (p.dxf ? 'retomado.dxf' : ''),
    ups: p.dxf && p.dxfMode !== 'nesting' ? 9 : (Number(p.ups) || 9),
    ruleLengthPerSheet: Number(p.ruleLengthPerSheet) || 2500,
    gap: p.gap != null ? Number(p.gap) : 0.5,
    nestingUps: p.dxfMode === 'nesting' ? (Number(p.ups) || 9) : 9,
    modo: p.modo || 'produccion', blanco: !!p.blanco, sides: Number(p.sides) || 1,
    cutEnabled: p.cutEnabled !== false, printEnabled: p.printEnabled !== false,
    piezasPorProducto: Number(p.piezasPorProducto) || 1,
    carril: p.carril === 'A_LA_MEDIDA' ? 'A_LA_MEDIDA' : 'STOCK',   // eje de sourcing (Slice 4)
    extras: p.extras || [],
  });
  const buildPayloads = (rots) => pieces.map((p, i) => piecePayload(p, (rots || rotations)[i]));

  // Spec sheet multipieza: una sección POR PIEZA con su geometría + preview gráfico (forma real del DXF).
  const handleSpecSheet = async () => {
    if (!result) return;
    setError('');
    try {
      const client = clients.find((c) => c.id === est.clientId);
      const specPieces = result.pieces.map((c, i) => {
        const src = pieces[i] || {};
        const mat = materials.find((m) => m.id === src.materialId);
        return {
          name: c.nombre || src.nombre || `Pieza ${i + 1}`,
          materialName: mat ? mat.name : '—',
          quantity: c.qtyPieza,
          ups: (result.geometry[i] && result.geometry[i].ups) || Number(src.ups) || null,
          ruleLengthPerSheet: Number(src.ruleLengthPerSheet) || null,
          geometry: result.geometry[i] || null,
          polys: src.dxfText ? parseDxf(src.dxfText) : [],
        };
      });
      await buildSpecSheetPdf({
        title: 'Spec sheet multipieza', refId: est.jobName || 'producto',
        clientName: client ? (client.name || client.companyName || '—') : (est.clientId ? '—' : 'Sin cliente'),
        jobName: est.jobName || '—',
        createdBy: user && user.displayName ? user.displayName : 'Sales',
        pieces: specPieces,
      });
    } catch (e) { setError(e.message || 'No se pudo generar el spec sheet.'); }
  };

  const validate = () => {
    if (!(Number(qtyProducto) > 0)) return 'La cantidad del producto debe ser > 0.';
    for (const p of pieces) {
      if (!p.materialId) return `Elegí material en "${p.nombre}".`;
      const isDxf = p.geometryMode === '1up' || p.geometryMode === 'nesting';
      if (isDxf && !p.dxfText) return `Subí el DXF en "${p.nombre}" o usá modo manual.`;
      if (p.geometryMode === 'manual' && (!p.ups || !p.ruleLengthPerSheet)) return `Faltan ups/rule en "${p.nombre}".`;
      if (p.geometryMode === 'nesting' && !(Number(p.nestingUps) >= 1)) return `Confirmá las piezas de la impo en "${p.nombre}".`;
    }
    return null;
  };

  // Paso 1→2: pide la geometría al servidor (el precio viene en la misma respuesta pero NO se
  // muestra hasta validar). Si TODAS las piezas son manuales (sin imposición), salta al precio.
  const handlePreview = async () => {
    setError(''); setResult(null); setEstResult(null); setEstError(''); setFluteOk(false);
    const v = validate(); if (v) { setError(v); return; }
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'getProductQuote');
      const res = await fn({ qtyProducto: Number(qtyProducto), pieces: buildPayloads(), extras });
      setResult(res.data);
      setStep(pieces.every((p) => p.geometryMode === 'manual') ? 'price' : 'preview');
    } catch (e) { setError(e.message || 'No se pudo cotizar el producto.'); }
    finally { setBusy(false); }
  };

  // Retomar (Slice 1 sourcing, bug 2): dispara el recosteo UNA vez cuando el loader terminó
  // de reconstruir el intake. Llega DESPUÉS del invalidador de preview (pieces cambiaron) y
  // entra por el flujo normal preview→precio (manual puro salta directo al precio).
  useEffect(() => {
    if (!autoQuote) return;
    setAutoQuote(false);
    if (pieces.some((p) => p.materialId)) handlePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoQuote]);

  // Rotación manual 90° de UNA pieza dentro del preview: recalcula y NO vuelve a 'input'; re-exige flauta.
  const handleRotatePiece = async (i) => {
    const nr = { ...rotations, [i]: !rotations[i] };
    setRotations(nr); setFluteOk(false); setError('');
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'getProductQuote');
      const res = await fn({ qtyProducto: Number(qtyProducto), pieces: buildPayloads(nr), extras });
      setResult(res.data);
    } catch (e) { setError(e.message || 'No se pudo recalcular la rotación.'); }
    finally { setBusy(false); }
  };

  // Slice 4 sourcing: override del comparativo — aplica el carril elegido a TODAS las piezas
  // elegibles (material con msfKey; las de stock fijo no cambian) y recotiza por el flujo
  // OBLIGATORIO (setPieces dispara el invalidador; autoQuote reentra preview → flauta → precio).
  const handleSourcingOverride = (carril) => {
    setPieces((ps) => ps.map((p) => (matById(p.materialId)?.msfKey ? { ...p, carril } : p)));
    setTimeout(() => setAutoQuote(true), 0);
  };

  // ── Fase 1: IA sobre la descripción (B1/C1). Solo pule texto EN MEMORIA — cero escritura;
  //    el resultado es una sugerencia editable (Usar / Descartar), la IA no decide nada.
  const handleImprove = async () => {
    if (!est.description.trim()) return;
    setEstError(''); setIa({ busy: true, suggestion: '' });
    try {
      const fn = httpsCallable(functions, 'improveText');
      const res = await fn({ text: est.description, lang: est.descriptionLang,
        context: 'descripción de una cotización/estimado de empaques para presentar al cliente' });
      setIa({ busy: false, suggestion: (res.data && res.data.improved) || '' });
    } catch (e) { setIa({ busy: false, suggestion: '' }); setEstError(e.message || 'La IA no respondió.'); }
  };
  const acceptIa = () => { setEst((p) => ({ ...p, description: ia.suggestion })); setIa({ busy: false, suggestion: '' }); };

  // ── Fase 1: guardar BORRADOR (acción deliberada; status draft, cero número YY-NNN).
  //    Devuelve el draftId para encadenar "Guardar versión". El preview sigue efímero.
  const saveDraft = async () => {
    if (!est.clientId) { setEstError('Elegí un cliente para guardar el borrador.'); return null; }
    if (!est.jobName.trim()) { setEstError('Pon un nombre de trabajo para guardar el borrador.'); return null; }
    const fn = httpsCallable(functions, 'saveEstimateDraft');
    const res = await fn({ draftId: draft.id || undefined, clientId: est.clientId, jobName: est.jobName.trim(),
      description: est.description, descriptionLang: est.descriptionLang });
    const id = res.data.draftId;
    setDraft((d) => ({ ...d, id, status: res.data.status || 'draft', msg: 'Borrador guardado (sin número).' }));
    return id;
  };
  const handleSaveDraft = async () => {
    setEstError(''); setBusy(true);
    try { await saveDraft(); }
    catch (e) { setEstError(e.message || 'No se pudo guardar el borrador.'); }
    finally { setBusy(false); }
  };

  // ── Fase 1: guardar VERSIÓN (snapshot completo server-side, con pricingConfigVersion).
  //    Sin borrador previo, lo crea primero (una sola acción deliberada del usuario).
  const handleSaveVersion = async () => {
    setEstError('');
    const v = validate(); if (v) { setEstError(v); return; }
    setBusy(true);
    try {
      const id = draft.id || await saveDraft();
      if (!id) return;
      const fn = httpsCallable(functions, 'saveEstimateVersion');
      const res = await fn({ draftId: id, qtyProducto: Number(qtyProducto), pieces: buildPayloads(), extras,
        markup: Number(est.markup) / 100 });
      setDraft((d) => ({ ...d, id, status: 'versioned', savedVersion: res.data.versionLabel,
        msg: `Versión ${res.data.versionLabel} guardada · $${Number(res.data.pricePerUnit || 0).toFixed(2)}/un · config rev ${res.data.pricingConfigVersion ? res.data.pricingConfigVersion.rev : '—'}.` }));
    } catch (e) { setEstError(e.message || 'No se pudo guardar la versión.'); }
    finally { setBusy(false); }
  };

  const handleCreateEstimate = async () => {
    setEstError(''); setEstResult(null);
    if (!est.clientId) { setEstError('Elegí un cliente.'); return; }
    if (!est.jobName.trim()) { setEstError('Poné un nombre de producto/trabajo.'); return; }
    const v = validate(); if (v) { setEstError(v); return; }
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'createEstimate');
      const res = await fn({
        qtyProducto: Number(qtyProducto), pieces: buildPayloads(), extras,
        markup: Number(est.markup) / 100, clientId: est.clientId, jobName: est.jobName.trim(),
        // Fase 1: si hay borrador en curso se PROMUEVE (mismo doc, conserva versiones);
        // la descripción de la cabecera viaja al estimado.
        draftId: draft.id || undefined,
        description: est.description || undefined, descriptionLang: est.descriptionLang,
      });
      setEstResult(res.data);
      if (draft.id) setDraft((d) => ({ ...d, status: 'approved', msg: '' }));
    } catch (e) { setEstError(e.message || 'No se pudo crear el estimado.'); }
    finally { setBusy(false); }
  };

  // PDF de estimado para CLIENTE (branded; sin costo/markup) — igual que el cotizador de 1 pieza.
  // El producto es UNA línea (qty = cantidad de producto, unit = precio de venta por producto).
  const estimatePdfData = () => {
    if (!estResult) return null;
    const qty = Number(qtyProducto) || 0;
    const mats = [...new Set(pieces.map((p) => materials.find((m) => m.id === p.materialId)?.name).filter(Boolean))];
    const specs = [`${pieces.length} pieza${pieces.length !== 1 ? 's' : ''}`, mats.join(' + ')].filter(Boolean).join(' · ');
    const subtotal = estResult.precioVenta || 0;
    const taxPct = 7; const tax = subtotal * taxPct / 100;
    const client = clients.find((c) => c.id === est.clientId);
    return {
      estimateNumber: estResult.estimateCode || estResult.estimateId,   // Fase 1: codigo YY-NNN en campo
      lang: est.descriptionLang,                                        // C1: labels del PDF en el idioma del toggle
      date: new Date().toLocaleDateString(est.descriptionLang === 'es' ? 'es-US' : 'en-US'),
      clientName: client?.name || client?.companyName || '—',
      clientLines: [client?.email].filter(Boolean),
      preparedBy: user?.displayName ? `${user.displayName} · Cartonbar` : undefined,
      lines: [{ description: [est.jobName || 'Custom packaging', est.description].filter(Boolean).join(' — '),
        specs, unit: estResult.precioVentaUnit || 0, qty, lineTotal: subtotal }],
      subtotal, taxPct, tax, total: subtotal + tax,
      // Slice 1 sourcing: dibujo de imposición en el PDF (mismo SVG puro que usa la CF).
      imposiciones: pieces.map((p2, i) => {
        const svg = impositionSvgString(result && result.geometry && result.geometry[i], piecePolys[i] || []);
        return svg ? { name: p2.nombre || ('Pieza ' + (i + 1)), svg } : null;
      }).filter(Boolean),
    };
  };
  // Slice 1 Tiers-PDF: el PDF lo renderiza el SERVIDOR (renderEstimatePdf: Puppeteer, US Letter,
  // persistido en Storage bajo el estimado, D-8). Acá solo se descarga. Sin window.print().
  const handleEstimatePdf = async () => {
    if (!estResult) return;
    setEstError(''); setBusy(true);
    try {
      const fn = httpsCallable(functions, 'renderEstimatePdf');
      const res = await fn({ estimateId: estResult.estimateId, lang: est.descriptionLang });
      downloadPdfBase64(res.data.pdfBase64, `${res.data.estimateCode || 'estimate'}-${res.data.lang}.pdf`);
    } catch (err) { setEstError(err.message || 'No se pudo generar el PDF del estimado.'); }
    finally { setBusy(false); }
  };

  const matOpt = (m) => `${m.name} — ${m.dimensions.width}×${m.dimensions.height}${m.dimensions.unit || 'in'}`;
  // ── Cascada de selección (Slice 2 PLAN-MATERIAL-SOURCING): familia → (flauta) → material.
  //    Gobernada por MATERIAL_TEMPLATES (taxonomía del catálogo) sobre los materiales vivos.
  //    Los filtros son UI-only (el payload sigue mandando materialId); si la pieza trae
  //    material (Retomar), los selects muestran lo DERIVADO del material elegido.
  const matById = (id) => materials.find((m) => m.id === id);
  const famOf = (p) => p.familia || (matById(p.materialId)?.category) || '';
  const fluteOf = (p) => p.fluteSel || (matById(p.materialId)?.attributes?.flute) || '';
  const familias = Object.keys(MATERIAL_TEMPLATES).filter((c) => materials.some((m) => m.category === c));
  const flutesDe = (fam) => {
    const enumOrden = (MATERIAL_TEMPLATES.CORRUGATED?.attributes || []).find((a) => a.field === 'flute')?.options || [];
    const vivas = new Set(materials.filter((m) => m.category === fam).map((m) => m.attributes?.flute).filter(Boolean));
    return enumOrden.filter((f) => vivas.has(f));
  };
  const materialesDe = (p) => {
    const fam = famOf(p); const fl = fluteOf(p);
    return materials.filter((m) =>
      (!fam || m.category === fam) &&
      (fam !== 'CORRUGATED' || !fl || m.attributes?.flute === fl));
  };
  // Polilíneas (forma real del DXF) por pieza para el preview gráfico del spec sheet.
  const piecePolys = useMemo(() => pieces.map((p) => (p.dxfText ? parseDxf(p.dxfText) : [])), [pieces]);

  return (
    <div className="p-4 md:p-6 h-full overflow-auto">
      <div className="mx-auto w-full" style={{ maxWidth: '1760px' }}>
        {/* Fase 1 §4.3 — FAST MODE (/quote): el MISMO multipieza con 1 línea y UI colapsada.
            Sin borradores ni versiones; solo preview efímero + Crear estimado. Mismo motor. */}
        <h1 className="text-3xl font-bold text-gray-800 mb-1">{fastMode ? 'Cotizador rápido' : 'Cotizador multipieza'}</h1>
        <p className="text-sm text-gray-500 mb-4">
          {fastMode ? (
            <>Un ítem, directo: <b>completá</b> la pieza → <b>validá</b> la imposición → <b>precio</b> → <b>Crear estimado</b>.
              Para multipieza, borradores y versiones usa el <button type="button" onClick={() => navigate('/quote-product')} className="text-blue-600 underline">Cotizador multipieza</button>.</>
          ) : (
            <>Producto = Σ piezas + extras. Flujo: <b>completá</b> las piezas → <b>validá</b> las imposiciones
              (y la flauta, en corrugado) → <b>precio</b>. El costo lo calcula el servidor; el markup lo elegís.</>
          )}
        </p>

        {/* Layout de 3 columnas (solo presentación): INTAKE · NESTING · COSTO. Colapsa a 1 col en angosto. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">

          {/* ════ COLUMNA 1 — INTAKE (definición / piezas) ════ */}
          <section className="bg-white rounded-xl shadow p-5">
            <div className="flex items-baseline gap-2 mb-4 pb-2 border-b">
              <span className="text-xs font-bold text-white bg-blue-600 rounded px-2 py-0.5">1</span>
              <h2 className="text-base font-bold text-gray-800">Definición</h2>
              <span className="text-xs font-semibold tracking-wide text-gray-400 uppercase">Intake</span>
            </div>
          <div className="space-y-4">
            {/* ── CABECERA (Fase 1 §2): cliente / trabajo / descripción — ANTES de las piezas.
                Ortogonal al router: no depende del método/sustrato de las piezas. ── */}
            <div className="border border-gray-200 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-600">Cliente</label>
                  <select value={est.clientId} onChange={(e) => setEst((p) => ({ ...p, clientId: e.target.value }))}
                    className="mt-1 w-full p-2 border border-gray-300 rounded-md text-sm">
                    <option value="">Elegí un cliente…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name || c.companyName || c.id}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-600">Nombre del trabajo</label>
                  <input type="text" placeholder="Nombre del producto/trabajo" value={est.jobName}
                    onChange={(e) => setEst((p) => ({ ...p, jobName: e.target.value }))}
                    className="mt-1 w-full p-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>
              {/* Fast mode: sin descripción/IA ni borradores — cabecera mínima (cliente + trabajo). */}
              {!fastMode && (
              <div>
                <div className="flex items-center justify-between">
                  <label className="block text-xs text-gray-600">Descripción</label>
                  <div className="flex items-center gap-1">
                    {/* C1: idioma de la descripción IA + labels del PDF. La UI queda en español. */}
                    {['es', 'en'].map((lg) => (
                      <button key={lg} type="button" onClick={() => setEst((p) => ({ ...p, descriptionLang: lg }))}
                        className={`px-2 py-0.5 text-xs rounded border uppercase ${est.descriptionLang === lg ? 'bg-indigo-100 border-indigo-400 font-semibold' : 'border-gray-300 text-gray-500'}`}>
                        {lg}
                      </button>
                    ))}
                    <button type="button" onClick={handleImprove} disabled={ia.busy || !est.description.trim()}
                      className="px-2 py-0.5 text-xs rounded border border-gray-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-40">
                      {ia.busy ? 'Mejorando…' : '✨ Mejorar con IA'}
                    </button>
                  </div>
                </div>
                <textarea rows={2} value={est.description}
                  onChange={(e) => setEst((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Descripción del trabajo — la IA la pule en el idioma elegido; también sale en el PDF."
                  className="mt-1 w-full p-2 border border-gray-300 rounded-md text-sm" />
                {ia.suggestion && (
                  <div className="mt-1 border border-indigo-200 bg-indigo-50/60 rounded p-2">
                    <p className="text-xs text-gray-500 mb-1">Sugerencia de la IA ({est.descriptionLang.toUpperCase()}) — puedes editar el campo después de usarla:</p>
                    <p className="text-sm whitespace-pre-wrap">{ia.suggestion}</p>
                    <div className="flex gap-2 mt-1">
                      <button type="button" onClick={acceptIa} className="px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded">Usar</button>
                      <button type="button" onClick={() => setIa({ busy: false, suggestion: '' })} className="px-2 py-1 text-xs border rounded">Descartar</button>
                    </div>
                  </div>
                )}
              </div>
              )}
              {!fastMode && (
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={handleSaveDraft} disabled={busy}
                  className="px-3 py-1.5 text-xs font-medium border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                  Guardar borrador
                </button>
                {draft.id && (
                  <span className="text-xs text-gray-500 font-mono">
                    borrador {draft.status === 'versioned' ? `· ${draft.savedVersion || 'versionado'}` : draft.status === 'approved' ? '· promovido' : '· sin número'}
                  </span>
                )}
                {draft.msg && <span className="text-xs text-green-700">{draft.msg}</span>}
              </div>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-3 flex items-end gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">Cantidad de producto</label>
                <input type="number" min="1" value={qtyProducto} onChange={(e) => setQtyProducto(e.target.value)}
                  className="mt-1 w-32 p-2 border border-gray-300 rounded-md text-center" />
              </div>
              <p className="text-xs text-gray-400">Cada pieza multiplica por su “piezas por producto”.</p>
            </div>

            {pieces.map((p, i) => {
              const cdMat = materials.find((m) => m.id === p.materialId);
              const cd = !!(cdMat && cdMat.category === 'CORRUGATED' && (cdMat.attributes?.flute || cdMat.msfKey));
              return (
              <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <input value={p.nombre} onChange={(e) => updPiece(i, 'nombre', e.target.value)}
                    className="flex-1 p-1.5 border border-gray-200 rounded font-semibold text-sm" />
                  {!fastMode && (<>
                    <button type="button" onClick={() => duplicatePiece(i)} title="Duplicar" className="text-xs px-2 py-1 border rounded">⧉</button>
                    <button type="button" onClick={() => removePiece(i)} title="Quitar" disabled={pieces.length === 1}
                      className="text-xs px-2 py-1 border rounded text-red-600 disabled:opacity-40">✕</button>
                  </>)}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {/* Cascada (Slice 2): tipo/familia → flauta (corrugado) → material filtrado. */}
                  <div>
                    <label className="block text-xs text-gray-600">Tipo de material</label>
                    <select value={famOf(p)}
                      onChange={(e) => setPieces((ps) => ps.map((x, j) => (j === i ? { ...x, familia: e.target.value, fluteSel: '', materialId: '' } : x)))}
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md">
                      <option value="">Todas las familias</option>
                      {familias.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600">Flauta</label>
                    <select value={fluteOf(p)} disabled={famOf(p) !== 'CORRUGATED'}
                      onChange={(e) => setPieces((ps) => ps.map((x, j) => (j === i ? { ...x, fluteSel: e.target.value, materialId: '' } : x)))}
                      className="mt-1 w-full p-2 border border-gray-300 rounded-md disabled:bg-gray-50 disabled:text-gray-400">
                      <option value="">{famOf(p) === 'CORRUGATED' ? 'Todas' : '—'}</option>
                      {famOf(p) === 'CORRUGATED' && flutesDe('CORRUGATED').map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-600">Material (hoja)</label>
                    <select value={p.materialId} onChange={(e) => updPiece(i, 'materialId', e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md">
                      <option value="">Elegí…</option>
                      {materialesDe(p).map((m) => <option key={m.id} value={m.id}>{matOpt(m)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600">Piezas por producto</label>
                    <input type="number" min="1" value={p.piezasPorProducto} onChange={(e) => updPiece(i, 'piezasPorProducto', e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md text-center" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600">Modo impresión</label>
                    <select value={p.modo} onChange={(e) => updPiece(i, 'modo', e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md">
                      {QUALITIES.length && ['draft', 'produccion', 'quality'].map((mo) => <option key={mo} value={mo}>{mo}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600">Caras</label>
                    <select value={p.sides} onChange={(e) => updPiece(i, 'sides', e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md">
                      <option value={1}>1 cara</option><option value={2}>2 caras</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-1.5 mt-5"><input type="checkbox" checked={p.blanco} onChange={(e) => updPiece(i, 'blanco', e.target.checked)} />Blanco</label>
                  <div className="col-span-2 flex gap-3 text-xs">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={p.cutEnabled} onChange={(e) => updPiece(i, 'cutEnabled', e.target.checked)} />Corte</label>
                    <label className="flex items-center gap-1"><input type="checkbox" checked={p.printEnabled} onChange={(e) => updPiece(i, 'printEnabled', e.target.checked)} />Impresión</label>
                    {['1up', 'nesting', 'manual'].map((gm) => (
                      <label key={gm} className="flex items-center gap-1"><input type="radio" name={`geom-${i}`} checked={p.geometryMode === gm} onChange={() => updPiece(i, 'geometryMode', gm)} />{gm}</label>
                    ))}
                    <label className="flex items-center gap-1 ml-auto">Carril:
                      <select value={p.carril || 'STOCK'} onChange={(e) => updPiece(i, 'carril', e.target.value)} className="p-1 border border-gray-200 rounded">
                        <option value="STOCK">Stock</option>
                        <option value="A_LA_MEDIDA" disabled={!cd}>A la medida</option>
                      </select>
                    </label>
                  </div>
                  {p.geometryMode === 'manual' ? (
                    <>
                      <div><label className="block text-xs text-gray-600">ups/hoja</label><input type="number" value={p.ups} onChange={(e) => updPiece(i, 'ups', e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md text-center" /></div>
                      <div><label className="block text-xs text-gray-600">rule/hoja</label><input type="number" value={p.ruleLengthPerSheet} onChange={(e) => updPiece(i, 'ruleLengthPerSheet', e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md text-center" /></div>
                    </>
                  ) : (
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-600">Archivo {p.geometryMode === 'nesting' ? 'imposición' : '1UP'} (.dxf)</label>
                      <input type="file" accept=".dxf,text/plain" onChange={(e) => onDxf(i, e)} className="mt-1 w-full text-xs" />
                      {p.dxfName && <span className="text-xs text-green-600">Cargado: {p.dxfName}</span>}
                      {p.geometryMode === 'nesting' && (
                        <input type="number" min="1" value={p.nestingUps} onChange={(e) => updPiece(i, 'nestingUps', e.target.value)} placeholder="piezas en la impo" className="mt-1 w-full p-2 border border-gray-300 rounded-md text-center" />
                      )}
                    </div>
                  )}
                </div>
                {/* Extras de rendimiento PROPIOS de esta pieza (fast mode los colapsa) */}
                {!fastMode && (
                <ExtrasEditor lines={p.extras || []} onAdd={() => addPieceExtra(i)} onUpd={(j, k, v) => updPieceExtra(i, j, k, v)} onRemove={(j) => removePieceExtra(i, j)}
                  title="Extras de esta pieza" hint="Sin extras propios (ej. mano de obra de esta pieza, pega)." totalLabel="Extras de la pieza" />
                )}
              </div>
              ); })}
            {!fastMode && (
            <button type="button" onClick={addPiece} className="w-full px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-lg hover:bg-indigo-100">+ Agregar pieza</button>
            )}

            {/* Extras DE PRODUCTO (B4 nivel 2): armado/ensamble FINAL que une las piezas — opcional */}
            {!fastMode && (
            <div className="border border-gray-200 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Armado de producto <span className="font-normal text-gray-400">(une las piezas — opcional)</span></h3>
              <ExtrasEditor lines={extras} onAdd={addExtra} onUpd={updExtra} onRemove={removeExtra}
                title="Extras del ensamble final" hint="Sin armado de producto. Agregá el ensamble que une las piezas (no pertenece a ninguna)." totalLabel="Armado / unidad" />
            </div>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}
            <button type="button" onClick={handlePreview} disabled={busy} className="w-full px-4 py-2.5 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-60">
              {busy ? 'Calculando…' : (pieces.every((p) => p.geometryMode === 'manual') ? 'Cotizar producto →' : 'Generar preview de imposiciones →')}
            </button>
          </div>
          </section>

          {/* ════ COLUMNA 2 — NESTING (imposiciones + validación) ════ */}
          <section className="bg-white rounded-xl shadow p-5">
            <div className="flex items-baseline gap-2 mb-4 pb-2 border-b">
              <span className="text-xs font-bold text-white bg-emerald-600 rounded px-2 py-0.5">2</span>
              <h2 className="text-base font-bold text-gray-800">Imposición</h2>
              <span className="text-xs font-semibold tracking-wide text-gray-400 uppercase">Nesting</span>
            </div>

            {!result ? (
              <div className="text-center text-gray-400 py-16">
                <p className="text-base">Acá van los previews de imposición de cada pieza (hoja, piezas, flauta).</p>
                <p className="text-sm mt-1">Completá las piezas (<b>INTAKE</b>) y tocá <b>Generar preview</b>.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {step === 'preview' && (
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-800">Validá las imposiciones</h2>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">precio oculto hasta validar</span>
                  </div>
                )}
                <div className="space-y-3">
                  {result.pieces.map((c, i) => {
                    const geo = result.geometry && result.geometry[i];
                    const polys = piecePolys[i] || [];
                    return (
                      <div key={i} className="border border-gray-100 rounded-lg p-2">
                        <p className="text-xs font-semibold text-gray-700 mb-1">
                          {c.nombre || `Pieza ${i + 1}`}
                          <span className="font-normal text-gray-400"> · carril {geo?.rail || pieces[i]?.carril?.toLowerCase() || 'stock'} · {(geo && geo.ups) || '—'} ups{geo?.hasFlute ? ` · flauta ${geo.flute || ''} a lo ${geo.fluteDir === 'x' ? 'ancho' : 'largo'}` : ''}</span>
                        </p>
                        {geo && geo.sheetW && polys.length ? (
                          <ImpositionSVG geo={geo} polys={polys} maxHeight="13rem" />
                        ) : (
                          <p className="text-xs text-gray-400 py-2">Sin DXF para dibujar (geometría manual).</p>
                        )}
                        {/* Rotación manual 90° + waste (orden 2.1) — solo 1UP, durante la validación. */}
                        {step === 'preview' && geo && geo.mode === '1up' && (
                          <div className="flex items-center justify-between mt-1 text-xs">
                            <span className="text-gray-500">
                              Orientación: <b>{geo.deg === 0 ? '0°' : '90°'}</b>{geo.wastePct != null ? ` · waste ~${Math.round(geo.wastePct)}%` : ''}
                            </span>
                            <button type="button" onClick={() => handleRotatePiece(i)} disabled={busy}
                              className="px-2 py-1 font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50">↻ Rotar 90°</button>
                          </div>
                        )}
                        {step === 'preview' && geo && geo.wasteAlert && (
                          <p className="mt-1 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded p-1.5">
                            ⚠️ Desperdicia ~{Math.round(geo.wastePct)}% de la hoja (umbral {Math.round(geo.wasteThreshold)}%). Rotá 90° o usá Nesting. No bloquea.
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
                {step === 'preview' && (
                <div className="border-t pt-3 space-y-2">
                  {(result.geometry || []).some((g) => g && g.hasFlute) ? (
                    <label className="flex items-start gap-2 text-sm cursor-pointer bg-blue-50 border border-blue-200 rounded-lg p-2">
                      <input type="checkbox" className="mt-0.5" checked={fluteOk} onChange={(e) => setFluteOk(e.target.checked)} />
                      <span>Confirmo que la <b>flauta corre en el sentido correcto</b> en todas las piezas de corrugado (ver dibujos).</span>
                    </label>
                  ) : (
                    <p className="text-xs text-gray-500">Ninguna pieza tiene dirección de flauta (rígido/honeycomb): no requiere validación de grano.</p>
                  )}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setStep('input')} className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">← Editar</button>
                    <button type="button" disabled={(result.geometry || []).some((g) => g && g.hasFlute) && !fluteOk} onClick={() => setStep('price')}
                      className="flex-1 px-4 py-2 font-bold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                      Confirmar y ver precio →
                    </button>
                  </div>
                </div>
                )}
                {step === 'price' && (
                  <button type="button" onClick={() => setStep('preview')} className="text-xs text-blue-600 underline">← volver a validar las imposiciones</button>
                )}
              </div>
            )}
          </section>

          {/* ════ COLUMNA 3 — COSTO (precio + auditoría + estimado) ════ */}
          <section className="bg-white rounded-xl shadow p-5">
            <div className="flex items-baseline gap-2 mb-4 pb-2 border-b">
              <span className="text-xs font-bold text-white bg-indigo-600 rounded px-2 py-0.5">3</span>
              <h2 className="text-base font-bold text-gray-800">Precio</h2>
              <span className="text-xs font-semibold tracking-wide text-gray-400 uppercase">Costo</span>
            </div>

            {!(step === 'price' && result) ? (
              <div className="text-center text-gray-400 py-16">
                <p className="text-base">Acá van el precio por markup, el costo por pieza (auditoría), el spec sheet y el estimado.</p>
                <p className="text-sm mt-1">Confirmá las imposiciones (columna <b>NESTING</b>) para verlos.</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-800">Precio del producto</h2>
                  {(result.geometry || []).some((g) => g && g.sheetW) && <button type="button" onClick={() => setStep('preview')} className="text-xs text-blue-600 underline">← ver imposiciones</button>}
                </div>
                {/* PRECIO según markup — DOMINANTE (CAMBIO 4) */}
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-2">Precio según markup — lo elige Comercial</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(result.precios || []).map((pr) => (
                      <div key={pr.markup} className={`rounded-lg p-2 text-center border ${pr.markup === 0.30 ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-emerald-200'}`}>
                        <div className="text-xs opacity-80">+{Math.round(pr.markup * 100)}%{pr.markup === 0.30 ? ' · mercado' : ''}</div>
                        <div className="text-xl font-extrabold tabular-nums">{money(pr.precio / Math.max(1, Number(qtyProducto)))}</div>
                        <div className="text-[11px] opacity-70">/un · total {money(pr.precio)}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">Costo (piso) {money(result.costoProductoUnit)}/un · total {money(result.costoProducto)} · {qtyProducto} productos · {result.pieces.length} piezas</p>
                </div>
                {/* Comparativo de SOURCING (Slice 4 PLAN-MATERIAL-SOURCING): el motor recosteó
                    AMBOS caminos; acá se muestran precio + lead time y el override. La decisión
                    es SIEMPRE del usuario (sugerencia, nunca automática). */}
                {result.sourcing && (() => {
                  const s = result.sourcing;
                  const eleg = pieces.filter((p) => matById(p.materialId)?.msfKey);
                  const enCut = eleg.length > 0 && eleg.every((p) => p.carril === 'A_LA_MEDIDA');
                  const lados = [
                    { key: 'STOCK', lado: s.stock, titulo: 'Hoja de stock', lead: 'produce hoy', activo: !enCut },
                    { key: 'A_LA_MEDIDA', lado: s.cutToSize, titulo: 'A la medida (SPP)', lead: `${s.leadTimeDays.min}–${s.leadTimeDays.max} días`, activo: enCut },
                  ];
                  return (
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Sourcing del material — stock vs. a la medida</p>
                      <div className="grid grid-cols-2 gap-2">
                        {lados.map((o) => (
                          <div key={o.key} className={`rounded-lg border p-2 text-center ${s.winner === o.key ? 'border-indigo-400 bg-white' : 'border-gray-200 bg-white/70'}`}>
                            <div className="text-xs text-gray-500">{o.titulo} · {o.lead}{s.winner === o.key ? ' · ★ sugerido' : ''}</div>
                            {o.lado.error ? (
                              <div className="text-xs text-red-500 mt-1">No disponible: {o.lado.error}</div>
                            ) : (<>
                              <div className="text-lg font-extrabold tabular-nums">{moneyU(o.lado.costoProductoUnit)}<span className="text-xs font-normal">/un</span></div>
                              <div className="text-[11px] text-gray-500">costo total {money(o.lado.costoProducto)}</div>
                            </>)}
                            <button type="button" disabled={busy || o.activo || !!o.lado.error} onClick={() => handleSourcingOverride(o.key)}
                              className={`mt-1 px-2 py-1 text-xs font-semibold rounded border ${o.activo ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-indigo-700 bg-white border-indigo-300 hover:bg-indigo-50 disabled:opacity-50'}`}>
                              {o.activo ? '✓ En uso' : 'Usar este'}
                            </button>
                          </div>
                        ))}
                      </div>
                      {typeof s.deltaUnit === 'number' && !s.stock.error && !s.cutToSize.error && (
                        <p className="text-xs text-gray-600 mt-2">
                          {s.deltaUnit > 0
                            ? `A la medida ahorra ${moneyU(s.deltaUnit)}/un, pero tarda ${s.leadTimeDays.min}–${s.leadTimeDays.max} días.`
                            : s.deltaUnit < 0
                              ? `Stock cuesta ${moneyU(-s.deltaUnit)}/un menos y produce hoy.`
                              : 'Mismo costo por unidad; stock produce hoy.'}
                          {' '}Cambiar de carril recotiza y vuelve a validar la imposición.
                        </p>
                      )}
                      {s.cutToSize.minimoAplica && s.cutToSize.excedente && (
                        <p className="mt-2 text-xs bg-amber-50 border border-amber-300 text-amber-800 rounded p-1.5">
                          ⚠️ Compra mínima SPP: el pedido consume {s.cutToSize.msfConsumido} MSF de {s.cutToSize.minimoMsf} mínimos.
                          Sobran ~{s.cutToSize.excedente.msf} MSF ({money(s.cutToSize.excedente.costoAprox)} aprox.) que entran a inventario
                          como stock — al producto se le cobra solo lo consumido.
                        </p>
                      )}
                    </div>
                  );
                })()}
                {/* Desglose de costo — SECUNDARIO / colapsable. Detalle por COMPONENTE por pieza (= cotizador interno). */}
                <details className="text-sm">
                  <summary className="cursor-pointer font-semibold text-gray-600">Costo por pieza + extras (auditoría · dos niveles)</summary>
                  <div className="mt-1 space-y-2">
                    {result.pieces.map((c, i) => (
                      <div key={i} className="border border-gray-100 rounded-lg p-2">
                        <div className="flex justify-between items-baseline">
                          <span className="text-gray-700 font-medium">{c.nombre || pieces[i]?.nombre || `Pieza ${i + 1}`} <span className="text-gray-400 font-normal text-xs">×{c.piezasPorProducto} · {c.qtyPieza}u · {c.sheets}h</span></span>
                          <span className="font-semibold tabular-nums">{moneyU(c.costoPiezaUnit)}/un</span>
                        </div>
                        <table className="w-full text-xs mt-1"><tbody className="divide-y divide-gray-100">
                          <CostComponentRows piece={c} totalLabel="Costo pieza (lote)" />
                        </tbody></table>
                        {(c.extras || []).map((x, xi) => (
                          <div key={`p${i}x${xi}`} className="flex justify-between text-xs pl-3 mt-0.5">
                            <span className="text-gray-500">↳ {x.descripcion || EXTRA_TIPOS.find((t) => t.value === x.tipo)?.label || 'extra'} <span className="text-gray-400">({x.tipo})</span></span>
                            <span className="text-emerald-700 tabular-nums">{moneyU(x.costoUn)}/un</span>
                          </div>
                        ))}
                      </div>
                    ))}
                    <table className="w-full text-sm"><tbody className="divide-y">
                      <tr className="font-semibold"><td className="py-1 text-gray-700">Costo directo / un (Σ piezas)</td><td className="py-1 text-right tabular-nums">{moneyU(result.costoDirectoUnit)}</td></tr>
                      {(result.extrasProducto || []).map((x, i) => (
                        <tr key={`a${i}`}><td className="py-0.5 pl-4 text-gray-500 text-xs">↳ armado: {x.descripcion || x.tipo} <span className="text-gray-400">({x.tipo})</span></td><td className="py-0.5 text-right text-emerald-700 text-xs tabular-nums">{moneyU(x.costoUn)}/un</td></tr>
                      ))}
                      {result.extrasProductoUnit > 0 && <tr className="font-semibold"><td className="py-1 text-gray-700">Armado de producto / un</td><td className="py-1 text-right text-emerald-700 tabular-nums">{moneyU(result.extrasProductoUnit)}</td></tr>}
                    </tbody></table>
                  </div>
                </details>

                {/* Spec sheet multipieza (B3): descarga PDF (los previews por pieza viven en NESTING). */}
                <div className="border-t pt-4 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-700">Spec sheet <span className="font-normal text-gray-400">· {est.jobName || 'producto'} · {result.pieces.length} piezas</span></h3>
                  <button type="button" onClick={handleSpecSheet} className="px-3 py-1.5 text-xs font-medium text-white bg-gray-800 rounded-lg hover:bg-gray-900">📄 Descargar spec sheet</button>
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Crear estimado</h3>
                  {estResult ? (
                    <div className="bg-green-50 rounded-lg p-3 text-sm space-y-1">
                      <p>Estimado <span className="font-mono font-semibold">{estResult.estimateCode || estResult.estimateId}</span> creado — aprobado.</p>
                      <p className="text-gray-700">Precio {money(estResult.precioVentaUnit)}/un · costo {money(estResult.costoUnit)}/un · markup +{Math.round((estResult.markup || 0) * 100)}% · margen {money(estResult.margen)}</p>
                      <div className="flex items-center gap-3 pt-1">
                        <button onClick={handleEstimatePdf} className="px-3 py-1.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800">📄 Descargar estimado (cliente)</button>
                        <button onClick={() => navigate('/estimates')} className="text-blue-600 underline">Ir a Estimados →</button>
                      </div>
                      {/* Preview = PDF: el iframe usa el MISMO buildEstimateHtml(data) que la descarga. */}
                      <div className="pt-3">
                        <p className="text-xs text-gray-500 mb-1">Vista previa (= lo que se descarga):</p>
                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-100" style={{ height: 360 }}>
                          <iframe title="Preview del estimado" srcDoc={buildEstimateHtml(estimatePdfData())}
                            style={{ width: '141%', height: '141%', border: 0, transform: 'scale(0.708)', transformOrigin: 'top left' }} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Fase 1: cliente y nombre del trabajo viven en la CABECERA (columna 1). */}
                      <p className="text-xs text-gray-400">
                        Cliente y nombre del trabajo se completan en la <b>cabecera</b> (columna 1).
                        {est.clientId && est.jobName ? '' : ' Faltan datos de cabecera.'}
                      </p>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-600">Markup</label>
                        <input type="number" min="0" value={est.markup} onChange={(e) => setEst((p) => ({ ...p, markup: e.target.value }))} className="w-20 p-2 border border-gray-300 rounded-md text-center text-sm" /><span className="text-sm">%</span>
                        {[20, 30, 40, 50].map((mk) => <button key={mk} type="button" onClick={() => setEst((p) => ({ ...p, markup: mk }))} className={`px-2 py-1 text-xs rounded border ${Number(est.markup) === mk ? 'bg-indigo-100 border-indigo-400' : 'border-gray-300'}`}>{mk}%</button>)}
                      </div>
                      {estError && <p className="text-xs text-red-600">{estError}</p>}
                      <div className="flex gap-2">
                        {/* Guardar versión: snapshot deliberado (re-cotiza server-side). Fast mode: sin versiones (§4.3). */}
                        {!fastMode && (
                          <button onClick={handleSaveVersion} disabled={busy}
                            className="flex-1 px-4 py-2 text-sm font-medium text-indigo-700 border border-indigo-300 rounded-lg hover:bg-indigo-50 disabled:opacity-60">
                            {busy ? '…' : 'Guardar versión'}
                          </button>
                        )}
                        <button onClick={handleCreateEstimate} disabled={busy}
                          className="flex-1 px-4 py-2 font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60">
                          {busy ? 'Creando…' : 'Crear estimado'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProductQuoteView;
